import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus } from "lucide-react";
import parse from "html-react-parser";
import FeaturedProductCard from "../HomePage/FeaturedProductCard";
import AlsoAvailableSection from "../HomePage/AlsoAvailableSection";
import { getProductBySlug, getProducts } from "../../services/productService";
import { useSyncedCart } from "../../hooks/useSyncedCart";
import { openCartDrawer } from "../../utils/cartDrawerEvents";
import { buildCartEventPayload, META_PIXEL_EVENTS, trackMetaEvent } from "../../utils/metaPixel";
import "./Product.css";

const FEATURE_ICONS = [
  { image: "/kuku-assets/images/1no-palm-oil.png", label: "NO PALM OIL" },
  { image: "/kuku-assets/images/1no-palm-oil.png", label: "NO PALM OIL" },
  { image: "/kuku-assets/images/1no-palm-oil.png", label: "NO PALM OIL" },
  { image: "/kuku-assets/images/1no-palm-oil.png", label: "NO PALM OIL" },
];

const STATIC_PRODUCTS = [
  {
    slug: "dry-chapti-kachori",
    name: "Jamnagari Chapti Kachori",
    category: "Kachori / Namkeen",
    description:
      "Products Description is here for describe about the prouct were it is used for.",
    hygieneInfo:
      "\"Lab-tested for safety and quality: suitable for all ages, from children to elders.\"",
    shelfLife: "180 days Self life",
    metaTitle: "Jamnagari Chapti Kachori | Kuku Namkeen",
    metaDescription:
      "Static product detail page styled to match the original product-info.html reference.",
    heroTheme: "yellow",
    relatedTheme: "yellow",
    titleTone: "blue",
    price: 300,
    badge: "5% OFF",
    weights: [
      { id: "250", label: "250 gm", price: 300 },
      { id: "500", label: "500 gm", price: 560 },
    ],
    stockBadges: [
      { label: "In Stock", tone: "success" },
      { label: "Out of Stock", tone: "danger" },
    ],
    images: [
      "/kuku-assets/products/dry-chapti-kachori/1 chapti kachori.jpg",
      "/kuku-assets/products/dry-chapti-kachori/2 chapti kachori.jpg",
      "/kuku-assets/products/dry-chapti-kachori/3 chapti kachori.jpg",
      "/kuku-assets/products/dry-chapti-kachori/4 chapti kachori.jpg",
    ],
    tabs: [
      {
        id: "ingredients",
        label: "Ingredients",
        content: "Ingredients shown here",
      },
      {
        id: "nutrition",
        label: "Nutrition Facts",
        content: "Nutrition shown here",
      },
      {
        id: "other-info",
        label: "Other Information",
        content: "Other information shown here",
      },
    ],
  },
  {
    slug: "dry-round-kachori",
    name: "Jamnagari Special Dry Kachori",
    category: "Kachori / Namkeen",
    description:
      "Dry kachori crafted in the signature Kuku style with a satisfying crunch and a strong traditional masala finish.",
    hygieneInfo:
      "\"Packed hygienically and sealed carefully to preserve freshness and flavour for everyday snacking.\"",
    shelfLife: "180 days Self life",
    metaTitle: "Jamnagari Special Dry Kachori | Kuku Namkeen",
    metaDescription:
      "Static product detail page for Jamnagari Special Dry Kachori.",
    heroTheme: "pink",
    relatedTheme: "pink",
    titleTone: "dark",
    price: 300,
    badge: null,
    weights: [
      { id: "250", label: "250 gm", price: 300 },
      { id: "500", label: "500 gm", price: 570 },
    ],
    stockBadges: [{ label: "In Stock", tone: "success" }],
    images: [
      "/kuku-assets/products/dry-round-kachori/1 dry-round.jpg",
      "/kuku-assets/products/dry-round-kachori/3 dry-round.jpg",
      "/kuku-assets/products/dry-round-kachori/4 dry-round.jpg",
      "/kuku-assets/products/dry-round-kachori/5 dry-round.jpg",
    ],
    tabs: [
      {
        id: "ingredients",
        label: "Ingredients",
        content: "Ingredients shown here",
      },
      {
        id: "nutrition",
        label: "Nutrition Facts",
        content: "Nutrition shown here",
      },
      {
        id: "other-info",
        label: "Other Information",
        content: "Other information shown here",
      },
    ],
  },
  {
    slug: "dry-fruit-kachori",
    name: "Dry Fruit Kachori",
    category: "Premium Kachori",
    description:
      "Premium dry fruit kachori with a festive profile, layered flavour and a rich crunch made for gifting or special occasions.",
    hygieneInfo:
      "\"Premium batches are packed under hygienic conditions to help preserve crispness and aroma.\"",
    shelfLife: "180 days Self life",
    metaTitle: "Dry Fruit Kachori | Kuku Namkeen",
    metaDescription: "Static product detail page for Dry Fruit Kachori.",
    heroTheme: "sky",
    relatedTheme: "sky",
    titleTone: "dark",
    price: 300,
    badge: null,
    weights: [{ id: "250", label: "250 gm", price: 300 }],
    stockBadges: [{ label: "In Stock", tone: "success" }],
    images: [
      "/kuku-assets/products/dryfruit/1 dry-fruit.jpg",
      "/kuku-assets/products/dryfruit/2 dry-fruit.jpg",
      "/kuku-assets/products/dryfruit/3 dry-fruit.png",
      "/kuku-assets/products/dryfruit/4 dry-fruit.jpg",
    ],
    tabs: [
      {
        id: "ingredients",
        label: "Ingredients",
        content: "Ingredients shown here",
      },
      {
        id: "nutrition",
        label: "Nutrition Facts",
        content: "Nutrition shown here",
      },
      {
        id: "other-info",
        label: "Other Information",
        content: "Other information shown here",
      },
    ],
  },
  {
    slug: "rajbhog-kachori",
    name: "Jamnagari Special Rajbhog Kachori",
    category: "Royal Kachori",
    description:
      "Rajbhog kachori offers a fuller premium taste with a richer finish while keeping the familiar Kuku identity intact.",
    hygieneInfo:
      "\"Carefully packed for freshness and product integrity during storage and delivery.\"",
    shelfLife: "180 days Self life",
    metaTitle: "Rajbhog Kachori | Kuku Namkeen",
    metaDescription: "Static product detail page for Rajbhog Kachori.",
    heroTheme: "yellow",
    relatedTheme: "yellow",
    titleTone: "dark",
    price: 300,
    badge: null,
    weights: [{ id: "250", label: "250 gm", price: 300 }],
    stockBadges: [{ label: "In Stock", tone: "success" }],
    images: [
      "/kuku-assets/products/rajbhog-kachori/1 rajbhog.jpg",
      "/kuku-assets/products/rajbhog-kachori/2 rajbhog.jpg",
      "/kuku-assets/products/rajbhog-kachori/3 rajbhog.jpg",
      "/kuku-assets/products/rajbhog-kachori/4 rajbhog.jpg",
    ],
    tabs: [
      {
        id: "ingredients",
        label: "Ingredients",
        content: "Ingredients shown here",
      },
      {
        id: "nutrition",
        label: "Nutrition Facts",
        content: "Nutrition shown here",
      },
      {
        id: "other-info",
        label: "Other Information",
        content: "Other information shown here",
      },
    ],
  },
];

function formatNumber(price) {
  const numericPrice = Number.parseFloat(price);

  if (Number.isFinite(numericPrice)) {
    return Intl.NumberFormat("en-IN").format(numericPrice);
  }

  return price || "0";
}

function normalizeApiImages(product) {
  const galleryImages = Array.isArray(product?.gallery_images)
    ? product.gallery_images
        .map((image) => {
          if (typeof image === "string") {
            return image;
          }

          return (
            image?.image_url ||
            image?.gallery_image ||
            image?.image ||
            image?.url ||
            ""
          );
        })
        .filter(Boolean)
    : [];

  return [
    ...new Set([product?.thumbnail_image, ...galleryImages].filter(Boolean)),
  ];
}

function normalizeApiWeights(product) {
  const sourceWeights =
    Array.isArray(product?.variant_data) && product.variant_data.length > 0
      ? product.variant_data
      : [product];

  return sourceWeights.map((weight, index) => ({
    id: `${product?.product_id || product?.slug || "product"}-${
      weight?.product_id || index
    }-${weight?.weight || weight?.item_weight || index}`,
    label: String(weight?.weight || weight?.item_weight || `Pack ${index + 1}`),
    product_id: weight?.product_id || product?.product_id,
    price:
      weight?.product_price ??
      product?.product_price ??
      weight?.product_sell_price ??
      product?.product_sell_price ??
      0,
    originalPrice:
      weight?.product_sell_price ??
      product?.product_sell_price ??
      0,
    discount: weight?.discount ?? product?.discount ?? product?.product_discount ?? 0,
  }));
}

function normalizeApiPerkIcons(product) {
  return [
    product?.perk_icon_1,
    product?.perk_icon_2,
    product?.perk_icon_3,
    product?.perk_icon_4,
  ]
    .filter(Boolean)
    .map((image, index) => ({
      image,
      label: `Product perk ${index + 1}`,
    }));
}

function formatShelfLife(value) {
  const shelfLife = String(value || "").trim();

  if (!shelfLife) {
    return "";
  }

  return /^\d+$/.test(shelfLife) ? `${shelfLife} days Self life` : shelfLife;
}

function mapApiProductToViewModel(product) {
  const weights = normalizeApiWeights(product);
  const stockCount = Number.parseInt(product?.stock_in_pkts, 10);
  const inStock = Number.isNaN(stockCount)
    ? product?.product_status === "Active"
    : stockCount > 0;
  const images = normalizeApiImages(product);
  const featureIcons = normalizeApiPerkIcons(product);

  return {
    product_id: product?.product_id,
    slug: product?.slug,
    name: product?.product_name || "Kuku Product",
    product_name: product?.product_name || "Kuku Product",
    thumbnail_image: product?.thumbnail_image,
    discount: product?.discount || product?.product_discount || 0,
    category:
      product?.category_name ||
      product?.product_type ||
      (product?.category_id
        ? `Category ${product.category_id}`
        : "Kuku Namkeen Product"),
    description:
      product?.product_description ||
      product?.meta_description ||
      "Product description will be updated soon.",
    hygieneInfo:
      product?.additional_details ||
      product?.product_description_two ||
      "Packed hygienically to preserve taste and freshness.",
    shelfLife:
      formatShelfLife(
        product?.self_life ||
          product?.shelf_life ||
          product?.shelfLife
      ),
    metaTitle:
      product?.meta_title ||
      `${product?.product_name || "Kuku Product"} | Kuku Namkeen`,
    metaDescription:
      product?.meta_description ||
      "Explore authentic Kuku Namkeen products online.",
    metaKeywords:
      product?.meta_keywords ||
      `${product?.product_name || "Kuku Product"}, Kuku Namkeen`,
    heroTheme: "yellow",
    relatedTheme: "yellow",
    titleTone: "dark",
    price:
      weights[0]?.price ||
      product?.product_price ||
      product?.product_sell_price ||
      0,
    originalPrice:
      weights[0]?.originalPrice ||
      product?.product_sell_price ||
      0,
    badge: product?.label_badge,
    weights,
    stockBadges: [
      {
        label: inStock ? "In Stock" : "Out of Stock",
        tone: inStock ? "success" : "danger",
      },
    ],
    images:
      images.length > 0
        ? images
        : ["/kuku-assets/products/dry-chapti-kachori/1 chapti kachori.jpg"],
    featureIcons: featureIcons.length > 0 ? featureIcons : FEATURE_ICONS,
    tabs: [
      {
        id: "ingredients",
        label: "Ingredients",
        content:
          product?.ingredients ||
          "Ingredients information will be updated soon.",
      },
      {
        id: "nutrition",
        label: "Nutrician Facts",
        type: "image",
        image: product?.nutrition_facts_image,
        content: "Nutrition facts will be updated soon.",
      },
      {
        id: "other-info",
        label: "Other Information",
        content:
          product?.other_information ||
          "Other product information will be updated soon.",
      },
    ],
  };
}

const formatMainPrice = (price) => `\u20b9 ${formatNumber(price)}`;

const formatCardPrice = (price) => `\u20b9 ${formatNumber(price)}/-`;

const HTML_PARSE_OPTIONS = {
  replace: (node) => {
    if (node?.type === "script" || node?.type === "style" || ["script", "style"].includes(node?.name)) {
      return <></>;
    }

    return undefined;
  },
};

function renderHtmlContent(content, fallback) {
  const html = String(content || fallback || "").trim();

  return parse(html || String(fallback || ""), HTML_PARSE_OPTIONS);
}

function renderTabContent(tab) {
  if (tab?.type === "image") {
    return tab.image ? (
      <img className="po-tab-image" src={tab.image} alt={tab.label} />
    ) : (
      renderHtmlContent(tab.content)
    );
  }

  return renderHtmlContent(tab?.content);
}

function ProductShowcase({
  image,
  images,
  selectedIndex = 0,
  title,
  large = false,
  fullImage = false,
}) {
  const sliderImages = Array.isArray(images) && images.length > 0 ? images : null;

  return (
    <div
      className={`po-showcase-card${large ? " po-showcase-card--large" : ""}${
        fullImage ? " po-showcase-card--full" : ""
      }${sliderImages ? " po-showcase-card--slider" : ""}`}
    >
      {sliderImages ? (
        <div
          className="po-showcase-track"
          style={{ transform: `translateX(-${selectedIndex * 100}%)` }}
        >
          {sliderImages.map((slideImage, index) => (
            <img
              key={`${slideImage}-${index}`}
              src={slideImage}
              alt={`${title} ${index + 1}`}
              className="po-showcase-image po-showcase-slide"
            />
          ))}
        </div>
      ) : (
        <img src={image} alt={title} className="po-showcase-image" />
      )}
    </div>
  );
}

function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addProduct, items: cartItems, updateQuantity } = useSyncedCart();
  const staticProduct =
    STATIC_PRODUCTS.find((item) => item.slug === slug) || null;

  const { data: productResponse, isLoading } = useQuery({
    queryKey: ["product-detail", slug],
    queryFn: () => getProductBySlug(slug),
    enabled: Boolean(slug),
  });

  const { data: relatedProductsResponse } = useQuery({
    queryKey: ["product"],
    queryFn: getProducts,
  });

  const apiProduct = productResponse?.data || null;
  const product = apiProduct ? mapApiProductToViewModel(apiProduct) : staticProduct;

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedWeightId, setSelectedWeightId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [activeTabId, setActiveTabId] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const imageResetKey = product?.images?.join("|") || "";
  const initialWeightId = product?.weights[0]?.id || "";
  const initialTabId = product?.tabs[0]?.id || "";

  useEffect(() => {
    if (!product?.slug) {
      return;
    }

    window.scrollTo(0, 0);
    setSelectedImageIndex(0);
    setSelectedWeightId(initialWeightId);
    setQuantity(1);
    setActiveTabId(initialTabId);
  }, [product?.slug, imageResetKey, initialWeightId, initialTabId]);
  const selectedWeight =
    product?.weights?.find((weight) => weight.id === selectedWeightId) ||
    product?.weights?.[0];

  const activeTab =
    product?.tabs?.find((tab) => tab.id === activeTabId) || product?.tabs?.[0];

  const activeProductId = selectedWeight?.product_id || product?.product_id || product?.id;
  const cartItem = cartItems.find(
    (item) => String(item.product_id) === String(activeProductId)
  );
  const cartQuantity = Number(cartItem?.quantity || 0);
  const isCartQuantityChanged = Boolean(cartItem) && quantity !== cartQuantity;
  const isCartButtonDisabled = isAdding || (Boolean(cartItem) && !isCartQuantityChanged);
  const cartButtonLabel = cartItem && !isCartQuantityChanged ? "Added to cart" : "Add to cart";

  useEffect(() => {
    setQuantity(cartItem ? cartQuantity : 1);
  }, [activeProductId, cartItem, cartQuantity]);
  useEffect(() => {
    const imageCount = product?.images?.length || 0;

    if (imageCount <= 1) {
      return undefined;
    }

    const sliderTimer = window.setInterval(() => {
      setSelectedImageIndex((currentIndex) => (currentIndex + 1) % imageCount);
    }, 3000);

    return () => window.clearInterval(sliderTimer);
  }, [imageResetKey, product?.images?.length]);
  if (isLoading && !product) {
    return (
      <>
        <Helmet>
          <title>Loading Product | Kuku Namkeen</title>
        </Helmet>

        <section className="po-page">
          <div className="po-container">
            <p>Loading product...</p>
          </div>
        </section>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Helmet>
          <title>Product Not Found | Kuku Namkeen</title>
          <meta
            name="description"
            content="The requested Kuku Namkeen product could not be found."
          />
        </Helmet>

        <section className="po-page">
          <div className="po-container">
            <nav className="po-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/categories">Products</Link>
              <span>/</span>
              <span className="po-breadcrumb-current">Not Found</span>
            </nav>

            <div className="po-white-panel">
              <h1>Product not found</h1>
              <p>We could not find that product right now.</p>
              <Link to="/categories" className="po-main-btn po-main-btn--blue">
                Browse Products
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }


  const relatedProductCards = apiProduct
    ? (relatedProductsResponse?.data || [])
        .filter((item) => item?.slug && item.slug !== product.slug)
        .slice(0, 3)
    : STATIC_PRODUCTS.filter((item) => item.slug !== product.slug)
        .slice(0, 3)
        .map((item) => ({
          img: item.images[0],
          title: item.name,
          price: item.weights[0]?.price || item.price || 0,
          priceLabel: formatCardPrice(item.weights[0]?.price || item.price || 0),
          badge: item.badge,
          weights:
            item.weights.length > 1
              ? item.weights.map((weight) => weight.label)
              : [],
          slug: item.slug,
        }));
  const featureIcons = product.featureIcons || FEATURE_ICONS;
  const currentPrice = selectedWeight?.price || product.price || 0;
  const originalPrice = selectedWeight?.originalPrice || product.originalPrice || 0;
  const showOriginalPrice = Number(originalPrice) > Number(currentPrice);

  const handleDecreaseQuantity = () => {
    setQuantity((current) => Math.max(cartItem ? 0 : 1, current - 1));
  };

  const handleIncreaseQuantity = () => {
    setQuantity((current) => current + 1);
  };

  const handleAddToCart = async () => {
    try {
      setIsAdding(true);
      if (cartItem) {
        if (!isCartQuantityChanged) {
          return true;
        }

        await updateQuantity(activeProductId, quantity);
        trackMetaEvent(
          META_PIXEL_EVENTS.ADD_TO_CART,
          buildCartEventPayload([
            {
              product_id: activeProductId,
              product_name: product?.product_name || product?.name,
              price: selectedWeight?.price || product?.price || product?.product_price,
              quantity,
            },
          ])
        );
        openCartDrawer();
        return true;
      }

      await addProduct(product, quantity, selectedWeight);
      return true;
    } finally {
      setIsAdding(false);
    }
  };

  const handleBuyNow = async () => {
    const didAdd = await handleAddToCart();

    if (didAdd) {
      navigate("/add-to-cart");
    }
  };

  return (
    <>
      <Helmet>
        <title>{product.metaTitle}</title>
        <meta name="description" content={product.metaDescription} />
        <meta name="keywords" content={product.metaKeywords} />
        <link
          rel="canonical"
          href={`https://kukunamkeen.in/product/${product.slug}`}
        />
      </Helmet>

      <section className="po-page">
        <div className="po-container">
          <nav className="po-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/categories">Products</Link>
            <span>/</span>
            <span className="po-breadcrumb-current">{product.name}</span>
          </nav>

          <div className="po-hero">
            <div className="po-gallery-column">
              <ProductShowcase
                images={product.images}
                selectedIndex={selectedImageIndex}
                title={product.name}
                large
                fullImage
              />

              <div className="po-thumb-strip">
                {product.images.map((image, index) => (
                  <button
                    key={`${image}-${index}`}
                    type="button"
                    className={`po-thumb-card${
                      selectedImageIndex === index ? " is-active" : ""
                    }`}
                    onClick={() => setSelectedImageIndex(index)}
                    aria-label={`View ${product.name} image ${index + 1}`}
                  >
                    <ProductShowcase
                      image={image}
                      title={`${product.name} ${index + 1}`}
                      fullImage
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="po-product-copy">
              <h1>{product.name}</h1>
              <p className="po-category">{product.category}</p>
              <div className="po-price">
                <span>{formatMainPrice(currentPrice)}/-</span>
                {showOriginalPrice && (
                  <span className="po-price-original">
                    {formatMainPrice(originalPrice)}/-
                  </span>
                )}
                
              </div>

              {product.weights.length > 0 && (
                <div className="po-inline-options">
                  <span className="po-inline-label">Weight :</span>
                  <div className="po-inline-pills">
                    {product.weights.map((weight) => (
                      <button
                        key={weight.id}
                        type="button"
                        className={`po-weight-pill${
                          selectedWeightId === weight.id ? " is-active" : ""
                        }`}
                        onClick={() => setSelectedWeightId(weight.id)}
                      >
                        {weight.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="po-stock-badges">
                {product.stockBadges.map((badge) => (
                  <span
                    key={`${badge.label}-${badge.tone}`}
                    className={`po-stock-badge po-stock-badge--${badge.tone}`}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>

              <div className="po-cart-bar">
                <div className="po-qty-box">
                  <button
                    type="button"
                    onClick={handleDecreaseQuantity}
                    aria-label="Decrease quantity"
                  >
                    <Minus size={18} />
                  </button>
                  <span>{quantity}</span>
                  <button
                    type="button"
                    onClick={handleIncreaseQuantity}
                    aria-label="Increase quantity"
                  >
                    <Plus size={18} />
                  </button>
                </div>

                <button
                  type="button"
                  className="po-main-btn po-main-btn--orange"
                  onClick={handleAddToCart}
                  disabled={isCartButtonDisabled}
                >
                  <span>{isAdding ? "Adding..." : cartButtonLabel}</span>
                </button>

                <button
                  type="button"
                  className="po-main-btn po-main-btn--blue"
                  onClick={handleBuyNow}
                  disabled={isAdding}
                >
                  Buy Now
                </button>
              </div>

              <div className="po-description">
                {renderHtmlContent(
                  product.description,
                  "Product description will be updated soon."
                )}

                <h2>Hygiene Information</h2>
                <p>{renderHtmlContent(product.hygieneInfo)}</p>

                {product.shelfLife && (
                  <>
                    <h2>Self Life</h2>
                    <p>{renderHtmlContent(product.shelfLife)}</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <section className="po-white-panel po-icon-panel">
            <div className="po-icon-grid">
              {featureIcons.map((feature, index) => (
                <div className="po-icon-item" key={`${feature.label}-${index}`}>
                  <div className="po-icon-circle">
                    <img src={feature.image} alt={feature.label} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="po-white-panel po-tab-panel-wrap">
            <div className="po-tab-layout">
              <div className="po-tab-nav" role="tablist" aria-label="Product information">
                {product.tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTabId === tab.id}
                    className={`po-tab-btn${
                      activeTabId === tab.id ? " is-active" : ""
                    }`}
                    onClick={() => setActiveTabId(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="po-tab-content" role="tabpanel">
                {renderTabContent(activeTab)}
              </div>
            </div>
          </section>
        </div>
      </section>

      <section className="po-related-section">
        <div className="po-container">
          <h2 className="po-section-title">YOU MAY ALSO LIKE</h2>

          <div className="po-related-grid">
            {relatedProductCards.map((relatedProduct) => (
              <FeaturedProductCard
                key={relatedProduct.product_id || relatedProduct.slug}
                product={relatedProduct}
              />
            ))}
          </div>
        </div>
      </section>

      <AlsoAvailableSection containerClassName="po-container" />
    </>
  );
}

export default Product;
